/**
 * Utilities for calculating rack dimensions and 3D positions
 */

import {
  RackPosition,
  RackDimensions,
  RackSummary,
  WarehouseDimensions,
  DEFAULT_RACK_DIMENSIONS
} from '../types/warehouseLayout';

// Bin size defaults (in meters) - can be fetched from API
const DEFAULT_BIN_WIDTH = 0.4;
const DEFAULT_BIN_DEPTH = 0.6;
const DEFAULT_BIN_HEIGHT = 0.3;
const SHELF_THICKNESS = 0.02;
const RACK_FRAME_WIDTH = 0.05;

/**
 * Calculate rack dimensions based on rack summary
 */
export function calculateRackDimensions(
  rackSummary: RackSummary,
  binWidth: number = DEFAULT_BIN_WIDTH,
  binDepth: number = DEFAULT_BIN_DEPTH,
  binHeight: number = DEFAULT_BIN_HEIGHT
): RackDimensions {
  const verticalCount = rackSummary.maxVertical - rackSummary.minVertical + 1;
  const shelfCount = rackSummary.maxShelf - rackSummary.minShelf + 1;

  return {
    width: verticalCount * binWidth + RACK_FRAME_WIDTH * 2,
    depth: binDepth + RACK_FRAME_WIDTH * 2,
    height: shelfCount * (binHeight + SHELF_THICKNESS) + SHELF_THICKNESS,
    verticals: verticalCount,
    shelves: shelfCount,
    drawers: undefined // Could be enhanced to detect from bin data
  };
}

/**
 * Calculate 3D position for a rack based on its 2D position
 */
export function calculate3DRackPosition(
  rackPosition: RackPosition,
  rackDimensions: RackDimensions
): [number, number, number] {
  // Convert from 2D floor position to 3D world coordinates
  // Three.js uses Y as up axis, so we swap Y and Z
  const x = rackPosition.x;
  const y = rackDimensions.height / 2; // Center vertically
  const z = rackPosition.y;

  return [x, y, z];
}

/**
 * Calculate bin position within a rack (relative to rack origin)
 */
export function calculateBinPosition(
  vertical: number,
  shelf: number,
  rackDimensions: RackDimensions,
  binWidth: number = DEFAULT_BIN_WIDTH,
  binHeight: number = DEFAULT_BIN_HEIGHT
): [number, number, number] {
  // Calculate position relative to rack center
  const totalWidth = rackDimensions.verticals * binWidth;
  const totalHeight = rackDimensions.shelves * (binHeight + SHELF_THICKNESS);

  const x = (vertical - 0.5) * binWidth - totalWidth / 2;
  const y = (shelf - 0.5) * (binHeight + SHELF_THICKNESS) - totalHeight / 2 + binHeight / 2;
  const z = 0;

  return [x, y, z];
}

/**
 * Apply rotation to rack dimensions (swap width/depth for 90° or 270°)
 */
export function getRotatedDimensions(
  dimensions: RackDimensions,
  rotation: 0 | 90 | 180 | 270
): { width: number; depth: number } {
  if (rotation === 90 || rotation === 270) {
    return { width: dimensions.depth, depth: dimensions.width };
  }
  return { width: dimensions.width, depth: dimensions.depth };
}

/**
 * Snap position to grid
 */
export function snapToGrid(value: number, gridUnit: number): number {
  return Math.round(value / gridUnit) * gridUnit;
}

/**
 * Check if a rack position is within warehouse bounds
 */
export function isWithinBounds(
  position: RackPosition,
  dimensions: RackDimensions,
  warehouseDimensions: WarehouseDimensions
): boolean {
  const rotated = getRotatedDimensions(dimensions, position.rotation);
  const halfWidth = rotated.width / 2;
  const halfDepth = rotated.depth / 2;

  return (
    position.x - halfWidth >= 0 &&
    position.x + halfWidth <= warehouseDimensions.length &&
    position.y - halfDepth >= 0 &&
    position.y + halfDepth <= warehouseDimensions.width
  );
}

/**
 * Check if two racks overlap
 */
export function racksOverlap(
  rack1: RackPosition,
  dim1: RackDimensions,
  rack2: RackPosition,
  dim2: RackDimensions,
  margin: number = 0.5 // Minimum gap between racks
): boolean {
  const rotated1 = getRotatedDimensions(dim1, rack1.rotation);
  const rotated2 = getRotatedDimensions(dim2, rack2.rotation);

  const halfW1 = rotated1.width / 2 + margin / 2;
  const halfD1 = rotated1.depth / 2 + margin / 2;
  const halfW2 = rotated2.width / 2 + margin / 2;
  const halfD2 = rotated2.depth / 2 + margin / 2;

  const overlapX = Math.abs(rack1.x - rack2.x) < (halfW1 + halfW2);
  const overlapY = Math.abs(rack1.y - rack2.y) < (halfD1 + halfD2);

  return overlapX && overlapY;
}

/**
 * Find optimal position for a new rack (avoid overlaps)
 */
export function findOptimalPosition(
  existingRacks: RackPosition[],
  existingDimensions: Map<string, RackDimensions>,
  newDimensions: RackDimensions,
  warehouseDimensions: WarehouseDimensions,
  gridUnit: number
): { x: number; y: number } | null {
  const rotated = getRotatedDimensions(newDimensions, 0);
  const startX = rotated.width / 2 + 1;
  const startY = rotated.depth / 2 + 1;

  // Try positions in a grid pattern
  for (let y = startY; y < warehouseDimensions.width - rotated.depth / 2; y += gridUnit * 4) {
    for (let x = startX; x < warehouseDimensions.length - rotated.width / 2; x += gridUnit * 4) {
      const testPosition: RackPosition = {
        rackNumber: 'test',
        x: snapToGrid(x, gridUnit),
        y: snapToGrid(y, gridUnit),
        rotation: 0
      };

      let hasCollision = false;
      for (const existingRack of existingRacks) {
        const existingDim = existingDimensions.get(existingRack.rackNumber) || DEFAULT_RACK_DIMENSIONS;
        if (racksOverlap(testPosition, newDimensions, existingRack, existingDim)) {
          hasCollision = true;
          break;
        }
      }

      if (!hasCollision) {
        return { x: testPosition.x, y: testPosition.y };
      }
    }
  }

  return null;
}

/**
 * Calculate distance between two points
 */
export function calculateDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

/**
 * Convert screen coordinates to world coordinates
 * Note: Must match the scale calculation in Layout2DCanvas draw() function
 */
export function screenToWorld(
  screenX: number,
  screenY: number,
  canvasWidth: number,
  canvasHeight: number,
  warehouseDimensions: WarehouseDimensions,
  zoom: number,
  panOffset: { x: number; y: number }
): { x: number; y: number } {
  // Use same padding as draw function (40px margin)
  const scale = Math.min(
    (canvasWidth - 40) / warehouseDimensions.length,
    (canvasHeight - 40) / warehouseDimensions.width
  ) * zoom;

  const offsetX = (canvasWidth - warehouseDimensions.length * scale) / 2 + panOffset.x;
  const offsetY = (canvasHeight - warehouseDimensions.width * scale) / 2 + panOffset.y;

  return {
    x: (screenX - offsetX) / scale,
    y: (screenY - offsetY) / scale
  };
}

/**
 * Convert world coordinates to screen coordinates
 * Note: Must match the scale calculation in Layout2DCanvas draw() function
 */
export function worldToScreen(
  worldX: number,
  worldY: number,
  canvasWidth: number,
  canvasHeight: number,
  warehouseDimensions: WarehouseDimensions,
  zoom: number,
  panOffset: { x: number; y: number }
): { x: number; y: number } {
  // Use same padding as draw function (40px margin)
  const scale = Math.min(
    (canvasWidth - 40) / warehouseDimensions.length,
    (canvasHeight - 40) / warehouseDimensions.width
  ) * zoom;

  const offsetX = (canvasWidth - warehouseDimensions.length * scale) / 2 + panOffset.x;
  const offsetY = (canvasHeight - warehouseDimensions.width * scale) / 2 + panOffset.y;

  return {
    x: worldX * scale + offsetX,
    y: worldY * scale + offsetY
  };
}
